/**
 * Turning a year of marked-as-unexpected spending into the one number spec §10 wants: what a
 * household should be putting aside each month.
 *
 * Pure, and deliberately blunt. The average is taken over the months the report COVERS, not
 * over the months that happened to have a surprise in them — a year with two bad months does
 * not need a twelfth of one bad month set aside every month, it needs a twelfth of both.
 *
 * Amounts arrive already summed per month and category from SQL; what happens here is
 * grouping and one division, which is arithmetic the screen shows rather than hides.
 */
import type { UnexpectedRow } from '../data/reportRepository.js';

export type UnexpectedMonth = Readonly<{
  /** YYYY-MM-DD, first of the month. */
  periodStart: string;
  total: number;
  rows: readonly UnexpectedRow[];
}>;

export type UnexpectedSummary = Readonly<{
  /** Newest month first, matching how the rows arrive. */
  months: readonly UnexpectedMonth[];
  total: number;
  /** Months the report looked back over, whether or not anything happened in them. */
  monthsCovered: number;
  /** total / monthsCovered, rounded to the đồng. The suggestion, not a rule. */
  suggestedMonthlyReserve: number;
  /** The single worst month — what a reserve has to survive, not just keep up with. */
  worstMonthTotal: number;
}>;

export function summariseUnexpected(
  rows: readonly UnexpectedRow[],
  monthsCovered: number,
): UnexpectedSummary {
  const byMonth = new Map<string, UnexpectedRow[]>();
  for (const row of rows) {
    const list = byMonth.get(row.periodStart) ?? [];
    list.push(row);
    byMonth.set(row.periodStart, list);
  }

  const months = [...byMonth.entries()]
    .map(([periodStart, monthRows]) => ({
      periodStart,
      total: monthRows.reduce((sum, row) => sum + row.amount, 0),
      rows: monthRows,
    }))
    .sort((left, right) => (left.periodStart > right.periodStart ? -1 : 1));

  const total = months.reduce((sum, month) => sum + month.total, 0);
  const covered = Math.max(1, monthsCovered);

  return {
    months,
    total,
    monthsCovered: covered,
    suggestedMonthlyReserve: Math.round(total / covered),
    worstMonthTotal: months.reduce((worst, month) => Math.max(worst, month.total), 0),
  };
}
